import { useState, useCallback, useRef } from 'react';
import { calculateDistance } from '../../shared/geoUtils';
import { LOCATION_FILTERING_CONFIG } from '../../shared/locationConfig';

interface LocationTrackingHookProps {
  sendMessage: (message: any) => void;
  onShowLocationConsent: (callback: () => void) => void;
  onLocationUpdate?: (location: CurrentLocationData) => void;
}

interface CurrentLocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  heading?: number;
  timestamp: number;
}

interface LocationPoint {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

// Client-side location filtering utility
const shouldSendLocation = (newPoint: LocationPoint, lastPoint: LocationPoint | null): boolean => {
  // Check accuracy using shared config
  if (newPoint.accuracy > LOCATION_FILTERING_CONFIG.CLIENT_MAX_ACCURACY) {
    console.log(`Rejecting location update: poor accuracy (${newPoint.accuracy}m > ${LOCATION_FILTERING_CONFIG.CLIENT_MAX_ACCURACY}m)`);
    return false;
  }
  
  // If no previous point, send this one
  if (!lastPoint) {
    return true;
  }
  
  // Time threshold using shared config
  const timeDiff = newPoint.timestamp - lastPoint.timestamp;
  if (timeDiff < LOCATION_FILTERING_CONFIG.CLIENT_MIN_TIME_DIFF) {
    console.log(`Rejecting location update: too frequent (${timeDiff}ms < ${LOCATION_FILTERING_CONFIG.CLIENT_MIN_TIME_DIFF}ms)`);
    return false;
  }
  
  // Distance threshold using shared config
  const distance = calculateDistance(
    lastPoint.latitude, lastPoint.longitude,
    newPoint.latitude, newPoint.longitude
  );
  
  if (distance < LOCATION_FILTERING_CONFIG.CLIENT_MIN_DISTANCE) {
    console.log(`Rejecting location update: too close (${distance.toFixed(1)}m < ${LOCATION_FILTERING_CONFIG.CLIENT_MIN_DISTANCE}m)`);
    return false;
  }
  
  return true;
};


export const useLocationTracking = ({ sendMessage, onShowLocationConsent, onLocationUpdate }: LocationTrackingHookProps) => {
  const [locationWatching, setLocationWatching] = useState(false);
  const [watchId, setWatchId] = useState<number | null>(null);
  const lastSentLocation = useRef<LocationPoint | null>(null);

  const startLocationTracking = useCallback(() => {
    if (navigator.geolocation) {
      const id = navigator.geolocation.watchPosition(
        (position) => {
          const { latitude, longitude, accuracy, heading } = position.coords;
          const timestamp = Date.now();
          
          // Always provide real-time location data to caller (for UI indicator)
          const currentLocationData: CurrentLocationData = {
            latitude,
            longitude,
            accuracy: accuracy || 999,
            heading: heading || undefined,
            timestamp
          };
          
          // Notify caller with current location (not filtered)
          onLocationUpdate?.(currentLocationData);
          
          const newPoint: LocationPoint = {
            latitude,
            longitude,
            accuracy: accuracy || 999, // Default to poor accuracy if not provided
            timestamp
          };
          
          // Client-side filtering: only send if point passes quality checks
          if (shouldSendLocation(newPoint, lastSentLocation.current)) {
            // Send location to server
            sendMessage({
              type: 'update_user_location',
              latitude,
              longitude,
              accuracy,
              timestamp
            });
            
            // Update last sent location
            lastSentLocation.current = newPoint;
          }
        },
        (error) => {
          console.error('Error tracking location:', error);
          setLocationWatching(false);
          setWatchId(null);
        },
        { 
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 30000
        }
      );
      
      setWatchId(id);
      setLocationWatching(true);
    }
  }, [sendMessage]);

  const stopLocationTracking = useCallback(() => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
    setLocationWatching(false);
    
    // Reset last sent location
    lastSentLocation.current = null;
    
    // Notify server that tracking stopped
    sendMessage({
      type: 'stop_user_location_tracking'
    });
  }, [watchId, sendMessage]);

  const handleLocationTrackingToggle = useCallback(() => {
    if (!locationWatching) {
      onShowLocationConsent(startLocationTracking);
    } else {
      stopLocationTracking();
    }
  }, [locationWatching, onShowLocationConsent, startLocationTracking, stopLocationTracking]);

  return {
    locationWatching,
    handleLocationTrackingToggle,
    startLocationTracking,
    stopLocationTracking
  };
};